/**
 * 🔐 Security Center
 * --------------------------------------------------------------
 * Live admin dashboard for trusted devices, active sessions, account
 * security, and audit logs. Polls every 10 seconds for real-time
 * visibility (no WebSockets needed for this iteration).
 */
import React, { useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import {
  Shield, ShieldAlert, Activity, Users, Smartphone, Ban, CheckCircle2,
  LogOut, Pause, Play, AlertTriangle, RefreshCw, Trash2, Globe,
} from 'lucide-react';
import { AuthContext, API, formatDateWithEnglishNumerals } from '../shared';

const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const SecurityCenter = () => {
  const { user } = useContext(AuthContext);
  const [tab, setTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [pendingDevs, setPendingDevs] = useState([]);
  const [trustedDevs, setTrustedDevs] = useState([]);
  const [blockedDevs, setBlockedDevs] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [confirm, setConfirm] = useState({ open: false });

  const isAdmin = user?.role === 'super_admin';

  const fetchAll = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      const [s, sess, pen, tru, blk, lg, us] = await Promise.all([
        axios.get(`${API}/security/dashboard`, auth()),
        axios.get(`${API}/security/sessions?only_active=true`, auth()),
        axios.get(`${API}/security/devices?status=pending`, auth()),
        axios.get(`${API}/security/devices?status=trusted`, auth()),
        axios.get(`${API}/security/devices?status=blocked`, auth()),
        axios.get(`${API}/security/logs?limit=100`, auth()),
        axios.get(`${API}/users`, auth()).catch(() => ({ data: [] })),
      ]);
      setStats(s.data);
      setSessions(sess.data || []);
      setPendingDevs(pen.data || []);
      setTrustedDevs(tru.data || []);
      setBlockedDevs(blk.data || []);
      setLogs(lg.data || []);
      setUsers(us.data || []);
    } catch (e) {
      console.error('security fetch failed', e);
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchAll();
    const t = setInterval(fetchAll, 10000); // 10s real-time refresh
    return () => clearInterval(t);
  }, [fetchAll]);

  if (!isAdmin) {
    return (
      <div className="p-6" dir="rtl">
        <Card>
          <CardContent className="p-12 text-center">
            <ShieldAlert className="h-12 w-12 mx-auto text-rose-500 mb-4" />
            <h2 className="text-xl font-bold">⛔ مركز الأمان للمدير العام فقط</h2>
          </CardContent>
        </Card>
      </div>
    );
  }

  const callApi = async (method, url, body = null, successMsg = null) => {
    try {
      const fn = method === 'delete' ? axios.delete : axios.post;
      await fn(`${API}${url}`, body || {}, auth());
      await fetchAll();
      // 🆕 Optional success toast — useful for unblock/approve where the
      // device moves to another tab and the user could think nothing
      // happened. We show a small floating alert so the action is
      // confirmed visibly.
      if (successMsg) {
        try {
          const div = document.createElement('div');
          div.textContent = successMsg;
          div.style.cssText = 'position:fixed;bottom:24px;right:24px;background:#16a34a;color:white;padding:12px 18px;border-radius:8px;box-shadow:0 6px 20px rgba(0,0,0,.2);z-index:9999;font-family:Tajawal,Arial,sans-serif;font-size:14px;';
          document.body.appendChild(div);
          setTimeout(() => div.remove(), 3500);
        } catch (_) { /* noop */ }
      }
    } catch (e) {
      alert(`❌ ${e.response?.data?.detail || e.message}`);
    }
  };

  const askConfirm = (title, message, onYes) => {
    setConfirm({ open: true, title, message, onYes });
  };

  return (
    <div className="p-6 space-y-6" dir="rtl" data-testid="security-center">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="h-7 w-7 text-brand-600" />
            🔐 مركز الأمان (Security Center)
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            ⚡ تحديث مباشر كلّ 10 ثوانٍ — مراقبة الجلسات، الأجهزة الموثوقة، والسلوك الأمني
          </p>
        </div>
        <Button variant="outline" onClick={fetchAll} disabled={loading}>
          <RefreshCw className={`h-4 w-4 ml-2 ${loading ? 'animate-spin' : ''}`} />
          تحديث
        </Button>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <StatCard icon={<Activity className="h-6 w-6" />} color="green" label="🟢 متصل الآن" value={stats?.online_count ?? '...'} />
        <StatCard icon={<Users className="h-6 w-6" />} color="blue" label="جلسات نشطة" value={stats?.active_sessions_total ?? '...'} />
        <StatCard icon={<Smartphone className="h-6 w-6" />} color="amber" label="🟡 أجهزة بانتظار" value={stats?.pending_devices ?? '...'} highlight={stats?.pending_devices > 0} />
        <StatCard icon={<CheckCircle2 className="h-6 w-6" />} color="emerald" label="✅ موثوقة" value={stats?.trusted_devices ?? '...'} />
        <StatCard icon={<Ban className="h-6 w-6" />} color="rose" label="⚫ محظورة" value={stats?.blocked_devices ?? '...'} />
        <StatCard icon={<AlertTriangle className="h-6 w-6" />} color="red" label="🔴 محاولات فاشلة (24س)" value={stats?.failed_logins_24h ?? '...'} />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid w-full grid-cols-5 max-w-3xl">
          <TabsTrigger value="dashboard" data-testid="tab-sessions">
            <Activity className="h-4 w-4 ml-1" /> الجلسات الحيّة
          </TabsTrigger>
          <TabsTrigger value="pending" data-testid="tab-pending-devs">
            <Smartphone className="h-4 w-4 ml-1" /> طلبات ({pendingDevs.length})
          </TabsTrigger>
          <TabsTrigger value="devices" data-testid="tab-trusted-devs">
            <CheckCircle2 className="h-4 w-4 ml-1" /> الأجهزة
          </TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users-control">
            <Users className="h-4 w-4 ml-1" /> الحسابات
          </TabsTrigger>
          <TabsTrigger value="logs" data-testid="tab-logs">
            <AlertTriangle className="h-4 w-4 ml-1" /> السجل الأمني
          </TabsTrigger>
        </TabsList>

        {/* Live Sessions */}
        <TabsContent value="dashboard">
          <Card>
            <CardHeader>
              <CardTitle>🟢 الجلسات النشطة الآن</CardTitle>
              <CardDescription>الموظفون المتصلون حاليًا — Force Logout يطبّق فورًا</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-right">الموظف</TableHead>
                    <TableHead className="text-right">الدور</TableHead>
                    <TableHead className="text-right">الحالة</TableHead>
                    <TableHead className="text-right">الجهاز</TableHead>
                    <TableHead className="text-right">IP</TableHead>
                    <TableHead className="text-right">بدأت</TableHead>
                    <TableHead className="text-right">آخر نشاط</TableHead>
                    <TableHead className="text-right">إجراءات</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sessions.length === 0 ? (
                    <TableRow><TableCell colSpan={8} className="text-center py-8 text-gray-500">لا توجد جلسات نشطة</TableCell></TableRow>
                  ) : sessions.map((s) => (
                    <TableRow key={s.jti} data-testid={`session-${s.jti}`}>
                      <TableCell className="font-medium">{s.user_name}</TableCell>
                      <TableCell><Badge variant="outline">{s.user_role}</Badge></TableCell>
                      <TableCell>
                        {s.online ? (
                          <Badge className="bg-green-100 text-green-800 animate-pulse">🟢 متصل</Badge>
                        ) : (
                          <Badge className="bg-gray-100 text-gray-700">😴 خامل</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-xs">{s.device_name || '—'}</TableCell>
                      <TableCell className="font-mono text-xs">{s.ip_address || '-'}</TableCell>
                      <TableCell className="text-xs">{formatDateWithEnglishNumerals(s.started_at)}</TableCell>
                      <TableCell className="text-xs text-gray-600">{formatDateWithEnglishNumerals(s.last_activity)}</TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 hover:bg-red-50"
                          onClick={() => askConfirm(
                            'إنهاء الجلسة',
                            `إنهاء جلسة ${s.user_name} الآن؟ سيخرج فوراً.`,
                            () => callApi('post', `/security/sessions/${s.jti}/kill`),
                          )}
                          data-testid={`kill-session-${s.jti}`}
                        >
                          <LogOut className="h-3 w-3 ml-1" /> إنهاء
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pending devices */}
        <TabsContent value="pending">
          <Card>
            <CardHeader>
              <CardTitle>🟡 أجهزة بانتظار الموافقة</CardTitle>
              <CardDescription>طلبات تسجيل دخول من أجهزة جديدة لم يتمّ توثيقها بعد</CardDescription>
            </CardHeader>
            <CardContent>
              <DeviceTable
                devices={pendingDevs}
                actions={[
                  { label: '✅ موافقة', icon: <CheckCircle2 className="h-3 w-3 ml-1" />, color: 'text-green-600 hover:bg-green-50',
                    onClick: (d) => askConfirm('موافقة الجهاز', `الموافقة على ${d.device_name}؟`, () => callApi('post', `/security/devices/${d.id}/approve`)) },
                  { label: '⛔ حظر', icon: <Ban className="h-3 w-3 ml-1" />, color: 'text-rose-600 hover:bg-rose-50',
                    onClick: (d) => askConfirm('حظر الجهاز', `حظر ${d.device_name}؟`, () => callApi('post', `/security/devices/${d.id}/block`)) },
                ]}
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Trusted + Blocked */}
        <TabsContent value="devices">
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-green-600" /> الأجهزة الموثوقة ({trustedDevs.length})</CardTitle>
                <CardDescription className="text-xs text-gray-500">
                  بعد إلغاء حظر جهاز، يظهر هنا تلقائياً وموثوقاً.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <DeviceTable
                  devices={trustedDevs}
                  actions={[
                    { label: '⛔ حظر', icon: <Ban className="h-3 w-3 ml-1" />, color: 'text-rose-600 hover:bg-rose-50',
                      onClick: (d) => askConfirm('حظر الجهاز', `حظر ${d.device_name}؟`, () => callApi('post', `/security/devices/${d.id}/block`, null, '⛔ تم حظر الجهاز')) },
                    { label: '🗑️ إلغاء', icon: <Trash2 className="h-3 w-3 ml-1" />, color: 'text-gray-600 hover:bg-gray-100',
                      onClick: (d) => askConfirm('إلغاء التوثيق', `حذف ${d.device_name}؟`, () => callApi('delete', `/security/devices/${d.id}`, null, '🗑️ تم حذف الجهاز')) },
                  ]}
                />
              </CardContent>
            </Card>

            {/* 🆕 Blocked devices section — ALWAYS visible (even when empty)
                so the admin always knows where to find the "unblock" button.
                Empty state explains the new device will reappear in the
                "Trusted devices" card above after un-blocking. */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Ban className="h-5 w-5 text-rose-600" /> الأجهزة المحظورة ({blockedDevs.length})</CardTitle>
                <CardDescription className="text-xs text-gray-500">
                  زر <b>"✅ إلغاء الحظر"</b> يفعّل الجهاز ويُرجعه إلى قسم <b>"الأجهزة الموثوقة"</b> أعلاه.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {blockedDevs.length === 0 ? (
                  <div className="text-center py-6 text-sm text-gray-500" data-testid="no-blocked-devices">
                    🟢 لا توجد أجهزة محظورة حالياً
                  </div>
                ) : (
                  <DeviceTable devices={blockedDevs} actions={[
                    {
                      label: '✅ إلغاء الحظر',
                      icon: <CheckCircle2 className="h-3 w-3 ml-1" />,
                      color: 'text-green-600 hover:bg-green-50',
                      onClick: (d) => askConfirm(
                        'إلغاء حظر الجهاز',
                        `سيُعاد تفعيل الجهاز "${d.device_name}" وينتقل إلى قسم "الأجهزة الموثوقة". هل تريد المتابعة؟`,
                        () => callApi('post', `/security/devices/${d.id}/approve`, null,
                          '✅ تم إلغاء الحظر — انظر قسم "الأجهزة الموثوقة" أعلاه'),
                      ),
                    },
                    {
                      label: '🗑️ حذف نهائي',
                      icon: <Trash2 className="h-3 w-3 ml-1" />,
                      color: 'text-gray-600 hover:bg-gray-100',
                      onClick: (d) => askConfirm(
                        'حذف نهائي',
                        `هذا سيحذف "${d.device_name}" بالكامل. عند تسجيل دخول جديد من الجهاز سيُعتبر جهازاً جديداً يحتاج موافقة.`,
                        () => callApi('delete', `/security/devices/${d.id}`, null, '🗑️ تم الحذف نهائياً'),
                      ),
                    },
                  ]} />
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Users control */}
        <TabsContent value="users">
          <Card>
            <CardHeader>
              <CardTitle>👥 التحكم في الحسابات</CardTitle>
              <CardDescription>إيقاف، Force Logout، إعادة تفعيل، أو إلغاء كلّ الأجهزة</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-right">الموظف</TableHead>
                    <TableHead className="text-right">البريد</TableHead>
                    <TableHead className="text-right">الدور</TableHead>
                    <TableHead className="text-right">الحالة</TableHead>
                    <TableHead className="text-right">إجراءات</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.filter((u) => u.id !== user?.id).map((u) => {
                    const suspended = u.is_active === false || u.security_status === 'suspended' || u.security_status === 'blocked';
                    return (
                      <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                        <TableCell className="font-medium">{u.name}</TableCell>
                        <TableCell className="text-xs text-gray-600">{u.email}</TableCell>
                        <TableCell><Badge variant="outline">{u.role}</Badge></TableCell>
                        <TableCell>
                          {suspended
                            ? <Badge className="bg-rose-100 text-rose-800">⛔ موقوف</Badge>
                            : <Badge className="bg-green-100 text-green-800">✅ نشط</Badge>}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1 flex-wrap">
                            <Button size="sm" variant="outline" className="text-orange-600 hover:bg-orange-50"
                              onClick={() => askConfirm('Force Logout', `إخراج ${u.name} من جميع الأجهزة؟`, () => callApi('post', `/security/users/${u.id}/force-logout`))}>
                              <LogOut className="h-3 w-3 ml-1" /> Force Logout
                            </Button>
                            {suspended ? (
                              <Button size="sm" variant="outline" className="text-green-600 hover:bg-green-50"
                                onClick={() => callApi('post', `/security/users/${u.id}/unsuspend`)}>
                                <Play className="h-3 w-3 ml-1" /> تفعيل
                              </Button>
                            ) : (
                              <Button size="sm" variant="outline" className="text-rose-600 hover:bg-rose-50"
                                onClick={() => askConfirm('إيقاف الحساب', `إيقاف ${u.name}؟ سيخرج فوراً ولن يستطيع تسجيل الدخول.`, () => callApi('post', `/security/users/${u.id}/suspend`, { reason: 'admin_action' }))}>
                                <Pause className="h-3 w-3 ml-1" /> إيقاف
                              </Button>
                            )}
                            <Button size="sm" variant="outline" className="text-gray-600 hover:bg-gray-100"
                              onClick={() => askConfirm('إلغاء كل الأجهزة', `حذف كل أجهزة ${u.name} الموثوقة؟`, () => callApi('post', `/security/users/${u.id}/revoke-all-devices`))}>
                              <Smartphone className="h-3 w-3 ml-1" /> إلغاء الأجهزة
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Logs */}
        <TabsContent value="logs">
          <Card>
            <CardHeader>
              <CardTitle>📋 السجلّ الأمني</CardTitle>
              <CardDescription>آخر 100 إجراء أمني</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-right">الحدث</TableHead>
                    <TableHead className="text-right">المستخدم</TableHead>
                    <TableHead className="text-right">IP</TableHead>
                    <TableHead className="text-right">التفاصيل</TableHead>
                    <TableHead className="text-right">الوقت</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((l, i) => (
                    <TableRow key={i}>
                      <TableCell>{eventBadge(l.event)}</TableCell>
                      <TableCell className="text-xs">{l.user_name || l.email || l.user_id || '-'}</TableCell>
                      <TableCell className="font-mono text-xs">{l.ip || l.ip_address || '-'}</TableCell>
                      <TableCell className="text-xs text-gray-600">
                        {l.reason ? `❗ ${l.reason}` : ''}
                        {l.device_id ? ` • device:${l.device_id.slice(0, 8)}` : ''}
                        {l.count !== undefined ? ` • ${l.count} sessions` : ''}
                      </TableCell>
                      <TableCell className="text-xs">{formatDateWithEnglishNumerals(l.at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Confirm dialog */}
      <Dialog open={confirm.open} onOpenChange={(o) => !o && setConfirm({ open: false })}>
        <DialogContent dir="rtl">
          <DialogHeader>
            <DialogTitle>{confirm.title}</DialogTitle>
            <DialogDescription className="text-base text-gray-700 pt-2">{confirm.message}</DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex flex-row-reverse gap-2">
            <Button onClick={async () => { const fn = confirm.onYes; setConfirm({ open: false }); if (fn) await fn(); }}
              className="bg-brand-600 hover:bg-brand-700 text-white">نعم</Button>
            <Button variant="outline" onClick={() => setConfirm({ open: false })}>إلغاء</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const StatCard = ({ icon, color, label, value, highlight }) => {
  const cls = {
    green: 'from-green-500 to-emerald-600',
    blue: 'from-brand-500 to-cyan-600',
    amber: 'from-amber-500 to-orange-500',
    emerald: 'from-emerald-500 to-teal-500',
    rose: 'from-rose-500 to-red-600',
    red: 'from-red-500 to-rose-700',
  }[color] || 'from-gray-500 to-gray-700';
  return (
    <Card className={`overflow-hidden ${highlight ? 'ring-2 ring-amber-400 animate-pulse' : ''}`}>
      <CardContent className="p-3">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[11px] text-gray-500">{label}</div>
            <div className="text-xl font-bold">{value}</div>
          </div>
          <div className={`p-2 rounded-lg bg-gradient-to-br ${cls} text-white shadow-md`}>{icon}</div>
        </div>
      </CardContent>
    </Card>
  );
};

const DeviceTable = ({ devices, actions = [] }) => (
  <Table>
    <TableHeader>
      <TableRow>
        <TableHead className="text-right">المستخدم</TableHead>
        <TableHead className="text-right">الجهاز</TableHead>
        <TableHead className="text-right">IP</TableHead>
        <TableHead className="text-right">آخر ظهور</TableHead>
        <TableHead className="text-right">Fingerprint</TableHead>
        <TableHead className="text-right">إجراءات</TableHead>
      </TableRow>
    </TableHeader>
    <TableBody>
      {devices.length === 0 ? (
        <TableRow><TableCell colSpan={6} className="text-center py-8 text-gray-500">لا توجد عناصر</TableCell></TableRow>
      ) : devices.map((d) => (
        <TableRow key={d.id} data-testid={`device-row-${d.id}`}>
          <TableCell className="text-sm">
            <div className="font-medium">{d.user_name || d.user_email}</div>
            <div className="text-xs text-gray-500">{d.user_email}</div>
          </TableCell>
          <TableCell className="text-xs">{d.device_name || '—'}</TableCell>
          <TableCell className="font-mono text-xs">{d.ip_address || '-'}</TableCell>
          <TableCell className="text-xs">{formatDateWithEnglishNumerals(d.last_seen_at || d.created_at)}</TableCell>
          <TableCell className="font-mono text-[10px] text-gray-500">{d.fingerprint?.slice(0, 18)}...</TableCell>
          <TableCell>
            <div className="flex gap-1 flex-wrap">
              {actions.map((a, i) => (
                <Button key={i} size="sm" variant="outline" className={a.color} onClick={() => a.onClick(d)} data-testid={`device-action-${a.label}-${d.id}`}>
                  {a.icon} {a.label}
                </Button>
              ))}
            </div>
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

const eventBadge = (e) => {
  const map = {
    login_success: <Badge className="bg-green-100 text-green-800">✅ دخول</Badge>,
    login_failed: <Badge className="bg-red-100 text-red-800">❌ فشل دخول</Badge>,
    login_blocked: <Badge className="bg-rose-200 text-rose-900">🚫 محظور</Badge>,
    device_pending: <Badge className="bg-amber-100 text-amber-800">🟡 جهاز جديد</Badge>,
    device_first_seen: <Badge className="bg-brand-100 text-brand-800">🔵 أوّل جهاز</Badge>,
    device_approved: <Badge className="bg-green-100 text-green-800">✅ موافقة</Badge>,
    device_blocked: <Badge className="bg-rose-100 text-rose-800">⛔ حظر جهاز</Badge>,
    device_revoked: <Badge className="bg-gray-200 text-gray-700">🗑️ إلغاء</Badge>,
    session_killed: <Badge className="bg-orange-100 text-orange-800">🔌 إنهاء جلسة</Badge>,
    force_logout: <Badge className="bg-orange-200 text-orange-900">⚡ Force Logout</Badge>,
    user_suspended: <Badge className="bg-red-100 text-red-800">⏸️ إيقاف حساب</Badge>,
    user_unsuspended: <Badge className="bg-green-100 text-green-800">▶️ تفعيل</Badge>,
    user_revoke_all_devices: <Badge className="bg-purple-100 text-purple-800">📵 إلغاء كل الأجهزة</Badge>,
  };
  return map[e] || <Badge variant="outline">{e}</Badge>;
};

export default SecurityCenter;
